import type React from "react";
import { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Grid,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { Email, ArrowBack, CheckCircle } from "@mui/icons-material";
import { toast } from "react-toastify";
import backgroundImage from "../../assets/backgroundImage.jpg";
import muzukuru from "../../assets/muzukuru.png";
import { motion } from "framer-motion";
import apiService from "../../service/Api";
import { easeOut, easeInOut } from "framer-motion";
import { showSuccessToast } from "../../components/common/ShowToast";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const navigate = useNavigate();

  const showErrorToast = (message: string, toastId: string = "error-toast") => {
    toast.dismiss(toastId);

    toast.error(message, {
      toastId,
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      style: {
        backgroundColor: "#fff",
        color: "#333",
        border: "1px solid #C29363",
        zIndex: 9999,
      },
      role: "alert",
      "aria-live": "assertive",
    } as any);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      showErrorToast("Please enter your email address.");
      return;
    }

    setIsLoading(true);
    try {
      await apiService.post(
        "/api/authentication/forgot-password/",
        { email },
        true
      );
      setIsEmailSent(true);
      showSuccessToast("Password reset link sent to your email.");
      setTimeout(() => navigate("/login"), 5000);
    } catch (err: any) {
      let errorMessage = "An error occurred. Please try again.";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message; // e.g., "Email not found."
      } else if (err.message?.includes("Network Error")) {
        errorMessage = "Network error. Please check your connection.";
      }
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const successVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: easeOut },
    },
  };

  return (
    <Grid container sx={{ minHeight: "100vh", overflow: "hidden" }}>
      {/* Left Column - Enhanced Image Background */}
      <Grid
        size={{ xs: false, md: 6 }}
        sx={{
          position: "relative",
          display: { xs: "none", md: "block" },
          background: `linear-gradient(135deg, rgba(97, 68, 56, 0.8) 0%, rgba(194, 147, 99, 0.6) 100%), url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(45deg, rgba(97, 68, 56, 0.1) 0%, transparent 50%)",
            zIndex: 1,
          },
        }}
      >
        {/* Floating geometric shapes */}
        <Box
          sx={{
            position: "absolute",
            top: "15%",
            right: "15%",
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: "rgba(194, 147, 99, 0.2)",
            zIndex: 2,
          }}
          component={motion.div}
          animate={{
            y: [0, -15, 0],
            rotate: [0, 90, 180],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: easeInOut,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            bottom: "20%",
            left: "20%",
            width: "50px",
            height: "50px",
            borderRadius: "8px",
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            zIndex: 2,
          }}
          component={motion.div}
          animate={{
            x: [0, 10, 0],
            rotate: [0, -90, -180],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: easeInOut,
          }}
        />

        {/* Enhanced Logo */}
        <Box
          sx={{
            position: "absolute",
            bottom: 40,
            left: 40,
            display: "flex",
            alignItems: "center",
            zIndex: 5,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "16px",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <img
                alt="Muzukuru logo"
                width="165px"
                height="70px"
                src={muzukuru || "/placeholder.svg"}
                style={{ cursor: "pointer" }}
              />
            </Paper>
          </motion.div>
        </Box>

        {/* Welcome text overlay */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            zIndex: 3,
            color: "white",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                background:
                  "linear-gradient(45deg, #fff 30%, rgba(194, 147, 99, 1) 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                fontWeight: 300,
              }}
            >
              Secure access to your account
            </Typography>
          </motion.div>
        </Box>
      </Grid>

      {/* Right Column - Enhanced Forgot Password Form */}
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 4, md: 8 },
          py: { xs: 4, md: 0 },
          background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 80% 20%, rgba(194, 147, 99, 0.05) 0%, transparent 50%)",
            zIndex: 1,
          },
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ width: "100%", maxWidth: "480px", zIndex: 2 }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px)",
              padding: "48px",
              borderRadius: "24px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #614438 0%, #C29363 100%)",
              },
            }}
            component={motion.div}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.3 }}
          >
            {!isEmailSent ? (
              <Box component="form" onSubmit={handleSubmit}>
                <motion.div variants={itemVariants}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Link
                      to="/login"
                      style={{
                        textDecoration: "none",
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        marginRight: "auto",
                      }}
                    >
                      <motion.div
                        whileHover={{ x: -5 }}
                        transition={{ duration: 0.2 }}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <ArrowBack sx={{ mr: 1, fontSize: "20px" }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Back to Sign In
                        </Typography>
                      </motion.div>
                    </Link>
                  </Box>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      background:
                        "linear-gradient(135deg, #614438 0%, #C29363 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      textAlign: "center",
                      marginBottom: "8px",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#666",
                      textAlign: "center",
                      marginBottom: "40px",
                      fontSize: "16px",
                      lineHeight: 1.6,
                    }}
                  >
                    No worries! Enter your email address and we'll send you a
                    link to reset your password.
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: "#333",
                        fontSize: "14px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Email Address
                    </Typography>
                    <TextField
                      placeholder="Enter your email address"
                      variant="outlined"
                      fullWidth
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email
                              sx={{
                                color:
                                  focusedField === "email" ? "#614438" : "#999",
                                transition: "color 0.3s",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          transition: "all 0.3s ease",
                          height: "56px",
                          "& fieldset": {
                            borderColor: "rgba(0,0,0,0.1)",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(194, 147, 99, 0.5)",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 25px rgba(97, 68, 56, 0.15)",
                            "& fieldset": {
                              borderColor: "#614438",
                            },
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "#333",
                          fontSize: "16px",
                        },
                      }}
                    />
                  </Box>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    sx={{
                      background:
                        "linear-gradient(135deg, #614438 0%, #C29363 100%)",
                      textTransform: "none",
                      padding: "16px",
                      borderRadius: "12px",
                      mb: 3,
                      fontSize: "16px",
                      fontWeight: 600,
                      boxShadow: "0 8px 25px rgba(97, 68, 56, 0.3)",
                      border: "none",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #503629 0%, #A67C52 100%)",
                        boxShadow: "0 12px 35px rgba(97, 68, 56, 0.4)",
                      },
                      "&:disabled": {
                        background: "rgba(97, 68, 56, 0.3)",
                      },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Divider sx={{ flex: 1, borderColor: "rgba(0,0,0,0.1)" }} />
                    <Typography
                      sx={{
                        px: 2,
                        color: "#999",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      or
                    </Typography>
                    <Divider sx={{ flex: 1, borderColor: "rgba(0,0,0,0.1)" }} />
                  </Box>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ color: "#666", fontSize: "15px" }}
                  >
                    Remember your password?{" "}
                    <Link
                      to="/login"
                      style={{
                        textDecoration: "none",
                        fontWeight: 600,
                        background:
                          "linear-gradient(135deg, #C29363 0%, #614438 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      <motion.span whileHover={{ scale: 1.05 }}>
                        Sign in
                      </motion.span>
                    </Link>
                  </Typography>
                </motion.div>
              </Box>
            ) : (
              // Success State
              <motion.div
                variants={successVariants}
                initial="hidden"
                animate="visible"
                style={{ textAlign: "center" }}
              >
                <Box sx={{ mb: 3 }}>
                  <motion.div
                    animate={{
                      rotate: [0, 360],
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: easeInOut,
                    }}
                  >
                    <CheckCircle
                      sx={{
                        fontSize: "80px",
                        color: "#614438",
                        filter: "drop-shadow(0 4px 8px rgba(97, 68, 56, 0.3))",
                      }}
                    />
                  </motion.div>
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg, #614438 0%, #C29363 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 2,
                  }}
                >
                  Check Your Email
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "#666",
                    mb: 4,
                    fontSize: "16px",
                    lineHeight: 1.6,
                  }}
                >
                  We've sent a password reset link to <strong>{email}</strong>.
                  Please check your inbox and follow the instructions to reset
                  your password.
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#999",
                      mb: 2,
                      fontSize: "14px",
                    }}
                  >
                    Didn't receive the email? Check your spam folder or
                  </Typography>

                  <Button
                    variant="outlined"
                    onClick={() => setIsEmailSent(false)}
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    sx={{
                      textTransform: "none",
                      borderColor: "rgba(97, 68, 56, 0.3)",
                      color: "#614438",
                      fontWeight: 600,
                      borderRadius: "8px",
                      "&:hover": {
                        borderColor: "#614438",
                        backgroundColor: "rgba(97, 68, 56, 0.04)",
                      },
                    }}
                  >
                    Try Again
                  </Button>
                </Box>

                <Link
                  to="/login"
                  style={{
                    textDecoration: "none",
                    fontWeight: 600,
                    background:
                      "linear-gradient(135deg, #C29363 0%, #614438 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: "15px",
                  }}
                >
                  <motion.span whileHover={{ scale: 1.05 }}>
                    ← Back to Sign In
                  </motion.span>
                </Link>
              </motion.div>
            )}
          </Paper>
        </motion.div>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;
